import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Layers, Cpu, Globe, ArrowRight, Zap, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "service-01",
  number: "01",
  title: "STRATEGIC PLANNING",
  description: "We architect robust digital ecosystems that drive engagement and conversions. Our data-led approach ensures your brand stays ahead in an evolving landscape with precision and foresight.",
  specs: ["Market Research", "Brand Audit", "Growth Strategy"],
  icon: Globe
}, {
  id: "service-02",
  number: "02",
  title: "EXPERIENCE DESIGN",
  description: "Creating seamless user journeys through intuitive interface design. We focus on visual clarity and functional excellence to deliver world-class digital experiences that resonate.",
  specs: ["Prototyping", "User Testing", "Visual Design"],
  icon: Layers
}, {
  id: "service-03",
  number: "03",
  title: "TECHNICAL EXECUTION",
  description: "High-performance engineering utilizing modern tech stacks. We build scalable, secure, and lightning-fast applications tailored to your specific business requirements and growth goals.",
  specs: ["React/Next.js", "API Integration", "Cloud Hosting"],
  icon: Cpu
}];
export default function OurServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="31346" id="services-overview" ref={ref} className={`bg-background py-24 relative overflow-hidden transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-12 lg:gap-24">
          {}
          <div className="relative">
            <div className="lg:sticky lg:top-32 flex flex-col gap-12">
              <div className="space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold tracking-wider uppercase text-foreground">
                  OUR EXPERTISE
                </h2>
                <p className="text-lg font-medium leading-relaxed text-muted-foreground max-w-md">
                  We combine technical precision with creative strategy to deliver
                  transformative digital solutions for modern enterprises.
                </p>
              </div>

              {}
              <nav className="hidden lg:flex flex-col gap-6">
                {services.map((service, i) => <Link key={service.id} to={`#${service.id}`} className="group flex items-center gap-6 w-fit" data-index={i}>
                    <div className="relative h-16 w-24 flex items-center justify-start">
                      {}
                      <svg className="w-full h-full overflow-visible" viewBox="0 0 80 40">
                        <text x="0" y="35" className="text-5xl font-bold fill-transparent stroke-muted-foreground/20 transition-all duration-300 group-hover:stroke-primary group-hover:translate-x-2" strokeWidth="1.5">
                          {service.number}
                        </text>
                      </svg>
                    </div>
                    <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors">
                      {service.title}
                    </span>
                  </Link>)}
              </nav>

              <div className="pt-8">
                <Button size="lg" className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg" asChild>
                  <Link to="/HomeInspectionServicePage">
                    START A PROJECT <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-16">
            {services.map((service, i) => <div key={service.id} id={service.id} className="scroll-mt-32" data-index={i}>
                <Card className="group relative overflow-hidden border-border bg-card/80 backdrop-blur-md shadow-xl transition-all duration-400 ease-out hover:border-primary">
                  {}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-0 h-0 bg-primary/5 rounded-full transition-all duration-700 ease-out group-hover:w-[250%] group-hover:h-[250%] pointer-events-none z-0" />

                  <CardHeader className="relative z-10 pb-2">
                    <div className="mb-6 flex items-center justify-between">
                      {}
                      <div className="relative flex size-16 items-center justify-center rounded-xl bg-accent/50 border border-border shadow-sm transition-transform duration-500 group-hover:scale-105 group-hover:border-primary/40">
                        <service.icon className="size-8 text-secondary" />
                        <div className="absolute top-3 right-3 size-2 rounded-full bg-primary shadow-[0_0_8px_rgba(249,115,22,0.5)]" />
                      </div>
                      <span className="font-mono text-4xl font-bold text-muted-foreground/5 select-none">
                        {service.number}
                      </span>
                    </div>
                    <CardTitle className="text-2xl font-bold uppercase tracking-widest text-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="relative z-10 space-y-8 pt-4">
                    <CardDescription className="text-lg font-medium leading-[1.6] text-muted-foreground">
                      {service.description}
                    </CardDescription>

                    <div className="space-y-3">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60">
                        TECHNICAL SPECIFICATIONS
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {service.specs.map((spec, j) => <span key={j} data-index={j} className="inline-flex items-center rounded-md border border-border bg-background/40 px-3 py-1 text-xs font-bold uppercase tracking-wider text-foreground transition-colors group-hover:border-primary/30 group-hover:bg-background/80">
                            {spec}
                          </span>)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}